/**
 * Reading and writing the intake flow (ACL-338).
 *
 * Its own module, not an addition to `queries.ts`, which is past three thousand lines: a file
 * near the limit is split before anything is added to it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useKeys } from "@/lib/queries";
import type { Graph, Question, Report } from "@/lib/intake-flow";
import type { FormFieldConfig, FormFieldType } from "@/lib/types";

export interface FlowSummary {
  id: number;
  tree_id: number;
  version: number;
  status: "draft" | "published" | "retired";
  title: string;
  note: string | null;
  start_key: string | null;
  state_field_key: string | null;
  published_at: string | null;
  /** The version this one's steps were rolled back from, when it was. */
  restored_from: number | null;
  created_at: string | null;
}

export interface FlowResponse extends FlowSummary {
  nodes: Graph["nodes"];
  edges: Graph["edges"];
  report: Report;
}

export interface IntakeStep {
  key: string;
  kind: string;
  title: string;
  field_key: string | null;
  status: string;
  /** The question's type, for a step that asks one this reader may see. */
  type: FormFieldType | null;
  /** A dropdown's or multi-select's choices. The key is what a condition compares. */
  options: { key: string; label: string }[];
  /** The field's own settings: a number's range, a text's length, a reference's target. */
  config: FormFieldConfig;
  /** What is stored today, so a completed card shows its answer and an edit starts from it. */
  value: unknown;
  /** The field's own `required`. Never set on a seeded intake field: whether the flow needs an
   *  answer is `must_answer`. */
  required: boolean;
  /** Whether the flow refuses a skip of this question (ACL-539). A server older than the field
   *  leaves it out, and that reads as true: the flow's own default, and never a Skip that fails. */
  must_answer?: boolean;
  /** Whether this reader may answer it. False when their role may read the field, not write it. */
  may_answer: boolean;
  /** False when the field is hidden from their role: the engine still routes on it, so the card
   *  says somebody else has to answer this rather than drawing an input it has no type for. */
  readable: boolean;
}

/** One branch the walk came down, named as the canvas names its lines. */
export interface IntakeTaken {
  from_key: string;
  position: number;
}

/**
 * What the walk knew about the lead besides their answers (ACL-339, ACL-340).
 *
 * A `Vary by firm or campaign` step is decided by these and by nothing a rep answers: the firm
 * comes from the lead's own tags, the campaign from the call they are on. A lead with neither
 * matches no branch and takes the Fallback — correct, and invisible until the panel could say so.
 */
export interface IntakeScope {
  firm_id: number | null;
  firm_label: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  language: string | null;
}

/** One supervisor flag a rule raised on the lead (ACL-341). */
export interface IntakeFlag {
  id: number;
  node_key: string;
  /** Null when the viewer ranks below `visible_to`: they are told the lead is flagged, not why. */
  reason: string | null;
  may_read_reason: boolean;
  visible_to: "supervisor" | "admin";
  raised_at: string | null;
  /** The rule stopped firing but nobody has closed it. It stays open for a person to decide. */
  stale: boolean;
  resolved_at: string | null;
  resolution: FlagResolution | null;
  note: string | null;
}

export type FlagResolution = "cleared" | "rejected" | "answer_changed";

export interface IntakeState {
  flow: FlowSummary | null;
  status: "in_progress" | "completed" | "rejected" | null;
  current: IntakeStep | null;
  outcome: IntakeStep | null;
  steps: IntakeStep[];
  /** The pinned version's graph, in the same shape the builder's routes send: the live canvas
   *  draws the version this run started on, not whatever is published now. */
  nodes: Graph["nodes"];
  edges: Graph["edges"];
  /** The branches this walk came down. Every other line on the board is unvisited. */
  taken: IntakeTaken[];
  /** The firm, campaign and language firm/campaign branches were judged against. */
  scope: IntakeScope;
  /** Field keys answered off the current route. `off_path` is the same answers, drawable. */
  also_answered: string[];
  /** Those off-route answers as steps, so a reader shows the question and not a field key
   *  (ACL-477). A field this reader may not see carries `readable: false` and no value.
   *
   *  Optional because a server from before ACL-477 does not send it, and because every reader
   *  here already treats "no off-route answers" and "the key is absent" the same way. */
  off_path?: IntakeStep[];
  steps_left: number;
  problem: string | null;
  outdated: boolean;
  /** Every open flag, stale ones included. */
  flags: IntakeFlag[];
}

function intakeKeys(prefix: readonly unknown[]) {
  return {
    flows: () => [...prefix, "intake-flows"] as const,
    // The tree bar reads which version each tree has published, so anything that publishes,
    // drafts or deletes has to mark it stale too: the two live under different keys, and the
    // badge said "Not published" over a tree that had just been published until a reload.
    trees: () => [...prefix, "intake-trees"] as const,
    flow: (id: number) => [...prefix, "intake-flows", id] as const,
    current: (treeId?: number) =>
      [...prefix, "intake-flows", "current", treeId ?? null] as const,
    questions: (id: number) => [...prefix, "intake-flows", id, "questions"] as const,
    leadIntake: (leadId: string) => [...prefix, "leads", "detail", leadId, "intake"] as const,
  };
}

function useIntakeKeys() {
  const keys = useKeys();
  return intakeKeys(keys.scoped(["intake"]).slice(0, -1));
}

/** Every version of one tree, newest first. Admin only. */
export function useFlows(treeId: number | undefined) {
  const keys = useIntakeKeys();
  return useQuery({
    queryKey: [...keys.flows(), "list", treeId ?? null],
    queryFn: () => api.get<FlowSummary[]>(`/intake-flows?tree_id=${treeId}`),
    enabled: treeId !== undefined,
  });
}

/** Roll a version back: its steps become a new draft to review and publish. */
export function useRestoreVersion() {
  const keys = useIntakeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (flowId: number) => api.post<FlowResponse>(`/intake-flows/${flowId}/restore`, {}),
    onSuccess: (flow) => {
      qc.setQueryData(keys.current(flow.tree_id), flow);
      void qc.invalidateQueries({ queryKey: keys.flows() });
      // The bar draws which tree has a draft, and this just made one.
      void qc.invalidateQueries({ queryKey: keys.trees() });
    },
  });
}

/**
 * What the builder opens on for one tree: its draft, else its published version.
 *
 * With no tree it is the tenant's first, which is what the page shows before anyone has picked
 * one (ACL-340).
 */
export function useCurrentFlow(treeId?: number) {
  const keys = useIntakeKeys();
  return useQuery({
    queryKey: keys.current(treeId),
    queryFn: () =>
      api.get<FlowResponse | null>(
        `/intake-flows/current${treeId ? `?tree_id=${treeId}` : ""}`,
      ),
  });
}

/** The questions the builder may attach: the contact form's own, with their options. */
export function useFlowQuestions(flowId: number | undefined) {
  const keys = useIntakeKeys();
  return useQuery({
    queryKey: keys.questions(flowId ?? 0),
    queryFn: () => api.get<Question[]>(`/intake-flows/${flowId}/questions`),
    enabled: flowId !== undefined,
  });
}

export function useCreateDraft() {
  const keys = useIntakeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (treeId?: number) =>
      api.post<FlowResponse>(`/intake-flows/draft${treeId ? `?tree_id=${treeId}` : ""}`, {}),
    onSuccess: (flow) => {
      qc.setQueryData(keys.current(flow.tree_id), flow);
      void qc.invalidateQueries({ queryKey: keys.flows() });
      void qc.invalidateQueries({ queryKey: keys.trees() });
    },
  });
}

/** Save the whole graph. A flow with problems saves; it is publishing that is refused. */
export function useSaveGraph() {
  const keys = useIntakeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flowId, graph }: { flowId: number; graph: Graph }) =>
      api.put<FlowResponse>(`/intake-flows/${flowId}/graph`, graph),
    onSuccess: (flow) => {
      qc.setQueryData(keys.current(flow.tree_id), flow);
      qc.setQueryData(keys.flow(flow.id), flow);
    },
  });
}

export function usePublishFlow() {
  const keys = useIntakeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flowId, note }: { flowId: number; note?: string }) =>
      api.post<FlowResponse>(`/intake-flows/${flowId}/publish`, { note: note ?? null }),
    onSuccess: (flow) => {
      qc.setQueryData(keys.current(flow.tree_id), flow);
      void qc.invalidateQueries({ queryKey: keys.flows() });
      void qc.invalidateQueries({ queryKey: keys.trees() });
    },
  });
}

export function useDeleteDraft() {
  const keys = useIntakeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (flowId: number) => api.delete<void>(`/intake-flows/${flowId}`),
    onSuccess: () => {
      // `flows()` is the prefix of every current-flow key, so one invalidation covers the
      // per-tree ones too (ACL-340).
      void qc.invalidateQueries({ queryKey: keys.flows() });
      void qc.invalidateQueries({ queryKey: keys.trees() });
    },
  });
}

/** Where a lead stands. `flow` is null when the tenant has published none. */
export function useLeadIntake(leadId: string | undefined) {
  const keys = useIntakeKeys();
  return useQuery({
    queryKey: keys.leadIntake(leadId ?? ""),
    queryFn: () => api.get<IntakeState>(`/leads/${leadId}/intake`),
    enabled: Boolean(leadId),
  });
}

/** Answer the question the flow is asking. A null value clears it, which is how Back works. */
export function useAnswerIntake(leadId: string) {
  const keys = useIntakeKeys();
  const qc = useQueryClient();
  const all = useKeys();
  return useMutation({
    mutationFn: (body: { field_key: string; value: unknown; call_id?: string | null }) =>
      api.post<IntakeState>(`/leads/${leadId}/intake/answers`, body),
    onSuccess: (state) => {
      qc.setQueryData(keys.leadIntake(leadId), state);
      // The answer is a form value, so the contact's field cards show it too.
      void qc.invalidateQueries({ queryKey: all.contactFields(leadId) });
    },
  });
}

/**
 * Re-read where the lead stands, after something the engine routes on changed elsewhere.
 *
 * Nothing is written but the run's own status: the path is recomputed from the lead's current
 * values (ADR-110), so setting a firm on the canvas reshapes the route without the intake
 * holding a second copy of it.
 */
export function useReplanIntake(leadId: string) {
  const keys = useIntakeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<IntakeState>(`/leads/${leadId}/intake/replan`, {}),
    onSuccess: (state) => qc.setQueryData(keys.leadIntake(leadId), state),
  });
}

export function useSkipIntake(leadId: string) {
  const keys = useIntakeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nodeKey: string) =>
      api.post<IntakeState>(`/leads/${leadId}/intake/skip`, { node_key: nodeKey }),
    onSuccess: (state) => qc.setQueryData(keys.leadIntake(leadId), state),
  });
}

/**
 * Close one flag in this person's own name (ACL-341).
 *
 * The route answers the whole intake state, so the card re-renders from one source rather than
 * patching the flag out of a list it holds separately.
 */
export function useResolveFlag(leadId: string) {
  const keys = useIntakeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flagId, resolution, note }: {
      flagId: number;
      resolution: FlagResolution;
      note?: string | null;
    }) =>
      api.post<IntakeState>(`/leads/${leadId}/intake/flags/${flagId}/resolve`, {
        resolution,
        note: note ?? null,
      }),
    onSuccess: (state) => qc.setQueryData(keys.leadIntake(leadId), state),
  });
}

export interface FormSectionSummary {
  key: string;
  title: string;
}

/** The contact form's sections, so a new question can say where it belongs. */
export function useLeadFormSections() {
  const keys = useIntakeKeys();
  return useQuery({
    queryKey: [...keys.flows(), "sections"],
    queryFn: async () => {
      const form = await api.get<{ sections: { key: string; title: string }[] }>("/forms/lead");
      return form.sections.map((s) => ({ key: s.key, title: s.title }));
    },
  });
}

/**
 * Add a question to the contact form from inside the builder.
 *
 * It is the same route the Custom fields screen uses, so the question is an ordinary field of the
 * contact form: it shows on the record, and any flow can ask it.
 */
export function useCreateQuestion(flowId: number | undefined) {
  const keys = useIntakeKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { section: string; title: string; type: string; config: unknown }) =>
      api.post<{ key: string; title: string }>("/forms/lead/fields", body),
    onSuccess: () => {
      if (flowId !== undefined) void qc.invalidateQueries({ queryKey: keys.questions(flowId) });
    },
  });
}
